import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchDataFromApi,
  postData,
  editData,
  deleteData,
} from "../../utils/api";

// Async thunks
export const fetchUserAddresses = createAsyncThunk(
  "address/fetchUserAddresses",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const response = await fetchDataFromApi(
        `/api/addresses/user?page=${page}&limit=${limit}`
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch addresses"
      );
    }
  }
);

export const createAddress = createAsyncThunk(
  "address/createAddress",
  async (addressData, { rejectWithValue }) => {
    try {
      const response = await postData("/api/addresses", addressData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create address"
      );
    }
  }
);

export const updateAddress = createAsyncThunk(
  "address/updateAddress",
  async ({ id, addressData }, { rejectWithValue }) => {
    try {
      const response = await editData(`/api/addresses/${id}`, addressData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update address"
      );
    }
  }
);

export const deleteAddress = createAsyncThunk(
  "address/deleteAddress",
  async (addressId, { rejectWithValue }) => {
    try {
      await deleteData(`/api/addresses/${addressId}`);
      return addressId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete address"
      );
    }
  }
);

export const setDefaultAddress = createAsyncThunk(
  "address/setDefaultAddress",
  async (addressId, { rejectWithValue }) => {
    try {
      const response = await editData(
        `/api/addresses/default/${addressId}`,
        {}
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to set default address"
      );
    }
  }
);

const initialState = {
  addresses: [],
  selectedAddress: null,
  defaultAddress: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalAddresses: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10,
  },
  loading: false,
  error: null,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  setDefaultLoading: false,
};

const addressSlice = createSlice({
  name: "address",
  initialState,
  reducers: {
    setSelectedAddress: (state, action) => {
      state.selectedAddress = action.payload;
    },
    clearSelectedAddress: (state) => {
      state.selectedAddress = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setDefaultAddressLocal: (state, action) => {
      // Update addresses to reflect new default
      state.addresses = state.addresses.map((address) => ({
        ...address,
        isDefault: address.id === action.payload,
      }));
      state.defaultAddress = state.addresses.find(
        (addr) => addr.id === action.payload
      );
    },
    addAddressLocal: (state, action) => {
      state.addresses.unshift(action.payload);
      if (action.payload.isDefault) {
        state.defaultAddress = action.payload;
      }
    },
    updateAddressLocal: (state, action) => {
      const index = state.addresses.findIndex(
        (addr) => addr.id === action.payload.id
      );
      if (index !== -1) {
        state.addresses[index] = action.payload;
        if (action.payload.isDefault) {
          state.defaultAddress = action.payload;
        }
      }
    },
    removeAddressLocal: (state, action) => {
      state.addresses = state.addresses.filter(
        (addr) => addr.id !== action.payload
      );
      if (state.defaultAddress?.id === action.payload) {
        state.defaultAddress = null;
      }
      if (state.selectedAddress?.id === action.payload) {
        state.selectedAddress = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user addresses
      .addCase(fetchUserAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserAddresses.fulfilled, (state, action) => {
        state.addresses = action.payload.data.addresses;
        state.pagination = action.payload.data.pagination;
        state.defaultAddress =
          action.payload.data.addresses.find((addr) => addr.isDefault) || null;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchUserAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create address
      .addCase(createAddress.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createAddress.fulfilled, (state, action) => {
        state.addresses.unshift(action.payload);
        if (action.payload.isDefault) {
          // Remove default from other addresses
          state.addresses = state.addresses.map((addr) =>
            addr.id === action.payload.id ? addr : { ...addr, isDefault: false }
          );
          state.defaultAddress = action.payload;
        }
        state.createLoading = false;
        state.error = null;
      })
      .addCase(createAddress.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })

      // Update address
      .addCase(updateAddress.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        const index = state.addresses.findIndex(
          (addr) => addr.id === action.payload.id
        );
        if (index !== -1) {
          state.addresses[index] = action.payload;
          if (action.payload.isDefault) {
            // Remove default from other addresses
            state.addresses = state.addresses.map((addr) =>
              addr.id === action.payload.id
                ? addr
                : { ...addr, isDefault: false }
            );
            state.defaultAddress = action.payload;
          }
        }
        state.updateLoading = false;
        state.error = null;
      })
      .addCase(updateAddress.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })

      // Delete address
      .addCase(deleteAddress.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.addresses = state.addresses.filter(
          (addr) => addr.id !== action.payload
        );
        if (state.defaultAddress?.id === action.payload) {
          state.defaultAddress = null;
        }
        if (state.selectedAddress?.id === action.payload) {
          state.selectedAddress = null;
        }
        state.deleteLoading = false;
        state.error = null;
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })

      // Set default address
      .addCase(setDefaultAddress.pending, (state) => {
        state.setDefaultLoading = true;
        state.error = null;
      })
      .addCase(setDefaultAddress.fulfilled, (state, action) => {
        // Remove default from all addresses
        state.addresses = state.addresses.map((addr) => ({
          ...addr,
          isDefault: addr.id === action.payload.id,
        }));
        state.defaultAddress = action.payload;
        state.setDefaultLoading = false;
        state.error = null;
      })
      .addCase(setDefaultAddress.rejected, (state, action) => {
        state.setDefaultLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedAddress,
  clearSelectedAddress,
  clearError,
  setDefaultAddressLocal,
  addAddressLocal,
  updateAddressLocal,
  removeAddressLocal,
} = addressSlice.actions;

export default addressSlice.reducer;
